import { isVideoAsset } from './normalize-video-urls';

/**
 * S3's 404 body is a few hundred bytes of XML; real videos are megabytes.
 * Video blobs below this are treated as broken, even without a MIME type
 * (older localForage entries can lose it). Not applied to images.
 */
export const MIN_VIDEO_BLOB_BYTES = 10 * 1024;

const ERROR_BODY_TYPE = /xml|html|json/i;
const VIDEO_TYPE = /^video\//i;
const M3U8_TYPE = /^application\/(vnd\.apple\.mpegurl|x-mpegurl)/i;

type MediaLike = { resource_type?: string, subType?: string, url?: string } | null | undefined;

/**
 * Validates a blob read from (or about to be written to) a media cache.
 * Rejects blobs that are empty, video blobs under MIN_VIDEO_BLOB_BYTES, and blobs
 * that look like an error body (e.g. the S3 XML 404 stored before NMP video URLs
 * were fixed). For videos, a set MIME type must be a video type (or an HLS
 * playlist type for m3u8). Callers should `removeItem` and refetch when this
 * returns false.
 */
export function isValidCachedBlob(blob: Blob | null | undefined, media?: MediaLike): boolean {
	if (!blob || blob.size === 0) {
		return false;
	}

	const isVideo = isVideoAsset(media);
	const isM3u8 = (media?.resource_type ?? media?.subType) === 'm3u8-video';
	const type = blob.type ?? '';

	// 1. Size (m3u8 playlists are legitimately tiny text files, so exempt)
	if (isVideo && !isM3u8 && blob.size < MIN_VIDEO_BLOB_BYTES) {
		return false;
	}

	// 2. Error-body MIME types
	if (ERROR_BODY_TYPE.test(type)) {
		return false;
	}

	// 3. Video MIME mismatch (only when a type is set)
	if (type && isVideo) {
		return VIDEO_TYPE.test(type) || (isM3u8 && M3U8_TYPE.test(type));
	}

	return true;
}

export default isValidCachedBlob;
